import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import html2canvas from 'html2canvas';
import SectionHeading from '../../components/common/SectionHeading';

const Wrapper = styled.section`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1.5rem;
  padding: 2.5rem;
  box-shadow: ${({ theme }) => theme.shadows.md};
  display: flex;
  flex-direction: column;
  gap: 3rem;

  @media (max-width: 768px) {
    padding: 1.75rem;
    gap: 2rem;
  }
`;

const ScheduleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const NoticeText = styled.p`
  margin: 1rem 0 0;
  padding: 1rem;
  background: ${({ theme }) => theme.colors.surfaceMuted};
  border-radius: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.875rem;
  line-height: 1.6;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 0.875rem;
  }
`;

const WeeklyScheduleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(7, 1fr);
    gap: 0.5rem;
  }
`;

const CalendarContainer = styled.div`
  margin-top: 1rem;
  background: ${({ theme }) => theme.colors.surfaceMuted};
  border-radius: 1rem;
  padding: 1.5rem;
  overflow-x: auto;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.5rem;
  min-width: 600px;
  width: 100%;

  @media (max-width: 768px) {
    gap: 0.25rem;
    min-width: 100%;
  }
`;

const CalendarHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  min-width: 600px;
  width: 100%;

  @media (max-width: 768px) {
    gap: 0.25rem;
    min-width: 100%;
  }
`;

const CalendarHeaderCell = styled.div`
  text-align: center;
  font-weight: 600;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  padding: 0.5rem;

  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.25rem;
  }
`;

const CalendarDayCell = styled.div`
  aspect-ratio: 1;
  background: ${({ theme, hasClass, isToday, isClosed, hasSpecialClass, isHoliday }) => 
    isClosed
      ? theme.colors.surfaceMuted
      : hasSpecialClass
        ? theme.colors.surface
        : isHoliday
          ? theme.colors.surfaceMuted
          : isToday 
            ? theme.colors.primary + '20'
            : hasClass 
              ? theme.colors.surface 
              : theme.colors.surfaceMuted};
  border: 2px solid ${({ theme, hasClass, isToday, isClosed, hasSpecialClass, isHoliday }) => 
    isClosed
      ? theme.colors.textMuted + '40'
      : hasSpecialClass
        ? theme.colors.primary + '30'
        : isHoliday
          ? theme.colors.textMuted + '20'
          : isToday 
            ? theme.colors.primary 
            : hasClass 
              ? theme.colors.primary + '30' 
              : theme.colors.textMuted + '20'};
  border-radius: 0.5rem;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 0.25rem;
  transition: ${({ theme }) => theme.transitions.default};
  position: relative;
  min-height: 100px;
  opacity: ${({ isClosed }) => isClosed ? 0.6 : 1};
  overflow: hidden;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  @media (max-width: 768px) {
    padding: 0.25rem;
    min-height: 80px;
    gap: 0.15rem;
  }
`;

const DayNumber = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  text-align: left;
  width: 100%;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  color: ${({ theme, hasClass, isToday, isClosed, isHoliday, dayOfWeek, hasSpecialClass }) => {
    if (isClosed && !isHoliday) {
      // 휴원일이지만 공휴일이 아닌 경우만 회색
      return theme.colors.textMuted;
    }
    if (isHoliday || dayOfWeek === 0) {
      // 법정공휴일 또는 일요일: 빨간색
      return '#ef4444';
    }
    if (dayOfWeek === 6) {
      // 토요일: 파란색
      return '#3b82f6';
    }
    if (isToday) {
      return theme.colors.primary;
    }
    if (hasClass || hasSpecialClass) {
      // 정규수업일 또는 특별수업일: 검정색
      return theme.colors.textPrimary;
    }
    return theme.colors.textMuted;
  }};
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.15rem;
  }
`;

const HolidayName = styled.span`
  font-size: 0.7rem;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 0.65rem;
    display: block;
    width: 100%;
  }
`;

const ClassBadges = styled.div`
  display: flex;
  flex-direction: ${({ useCompact }) => useCompact ? 'row' : 'column'};
  flex-wrap: ${({ useCompact }) => useCompact ? 'wrap' : 'nowrap'};
  gap: 0.25rem;
  width: 100%;
  align-items: center;
  justify-content: ${({ useCompact }) => useCompact ? 'center' : 'flex-start'};
  margin-top: auto;
  min-width: 0;
  max-width: 100%;

  @media (max-width: 768px) {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.15rem;
  }
`;

const CalendarBadge = styled.span`
  display: inline-block;
  padding: 0.15rem 0.4rem;
  border-radius: 999px;
  font-size: 0.6rem;
  font-weight: 600;
  background: ${({ theme, type }) => 
    type === 'elementary' 
      ? `${theme.colors.primary}20` 
      : type === 'middleHigh'
      ? `${theme.colors.secondary}20`
      : type === 'special'
      ? `${theme.colors.accent}20`
      : `${theme.colors.textMuted}20`};
  color: ${({ theme, type }) => 
    type === 'elementary' 
      ? theme.colors.primary 
      : type === 'middleHigh'
      ? theme.colors.secondary
      : type === 'special'
      ? theme.colors.accent
      : theme.colors.textMuted};
  border: 1px solid ${({ theme, type }) => 
    type === 'elementary' 
      ? `${theme.colors.primary}40` 
      : type === 'middleHigh'
      ? `${theme.colors.secondary}40`
      : type === 'special'
      ? `${theme.colors.accent}40`
      : `${theme.colors.textMuted}40`};
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    font-size: 0.5rem;
    padding: 0.1rem 0.3rem;
  }
`;

const EventBadge = styled.span`
  display: inline-block;
  padding: 0.15rem 0.4rem;
  border-radius: 999px;
  font-size: 0.6rem;
  font-weight: 600;
  background: ${({ theme, eventType }) => 
    eventType === 'exam' 
      ? `${theme.colors.accent}20` 
      : eventType === 'lecture'
      ? `#10b98120`
      : eventType === 'special'
      ? `#ec489920`
      : eventType === 'holiday'
      ? `${theme.colors.textMuted}20`
      : `${theme.colors.textMuted}20`};
  color: ${({ theme, eventType }) => 
    eventType === 'exam' 
      ? theme.colors.accent 
      : eventType === 'lecture'
      ? '#10b981'
      : eventType === 'special'
      ? '#ec4899'
      : eventType === 'holiday'
      ? theme.colors.textMuted
      : theme.colors.textMuted};
  border: 1px solid ${({ theme, eventType }) => 
    eventType === 'exam' 
      ? `${theme.colors.accent}40` 
      : eventType === 'lecture'
      ? '#10b98140'
      : eventType === 'special'
      ? '#ec489940'
      : eventType === 'holiday'
      ? `${theme.colors.textMuted}40`
      : `${theme.colors.textMuted}40`};
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    font-size: 0.5rem;
    padding: 0.1rem 0.3rem;
  }
`;

const EmptyDay = styled.div`
  aspect-ratio: 1;
  background: transparent;
  border-radius: 0.5rem;
  min-height: 80px;

  @media (max-width: 768px) {
    min-height: 60px;
  }
`;

const CalendarHeaderControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding: 0 0.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
`;

const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.surfaceMuted};

  @media (max-width: 768px) {
    gap: 0.75rem;
    padding: 0.75rem;
  }
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textPrimary};

  @media (max-width: 768px) {
    font-size: 0.7rem;
    gap: 0.4rem;
  }
`;

const LegendBadge = styled.span`
  display: inline-block;
  padding: 0.15rem 0.4rem;
  border-radius: 999px;
  font-size: 0.6rem;
  font-weight: 600;
  white-space: nowrap;
  box-sizing: border-box;
  background: ${({ theme, type, eventType }) => {
    if (type === 'elementary') return `${theme.colors.primary}20`;
    if (type === 'middle') return `${theme.colors.secondary}20`;
    if (type === 'high') return `${theme.colors.secondary}20`;
    if (eventType === 'exam') return `${theme.colors.accent}20`;
    if (eventType === 'lecture') return `#10b98120`;
    if (eventType === 'special') return `#ec489920`;
    return `${theme.colors.textMuted}20`;
  }};
  color: ${({ theme, type, eventType }) => {
    if (type === 'elementary') return theme.colors.primary;
    if (type === 'middle') return theme.colors.secondary;
    if (type === 'high') return theme.colors.secondary;
    if (eventType === 'exam') return theme.colors.accent;
    if (eventType === 'lecture') return '#10b981';
    if (eventType === 'special') return '#ec4899';
    return theme.colors.textMuted;
  }};
  border: 1px solid ${({ theme, type, eventType }) => {
    if (type === 'elementary') return `${theme.colors.primary}40`;
    if (type === 'middle') return `${theme.colors.secondary}40`;
    if (type === 'high') return `${theme.colors.secondary}40`;
    if (eventType === 'exam') return `${theme.colors.accent}40`;
    if (eventType === 'lecture') return '#10b98140';
    if (eventType === 'special') return '#ec489940';
    return `${theme.colors.textMuted}40`;
  }};

  @media (max-width: 768px) {
    font-size: 0.5rem;
    padding: 0.1rem 0.3rem;
  }
`;

const LegendLabel = styled.span`
  @media (max-width: 768px) {
    font-size: 0.65rem;
  }
`;

const MonthSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const MonthButton = styled.button`
  background: ${({ theme }) => theme.colors.surface};
  border: 2px solid ${({ theme }) => theme.colors.primary}30;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    background: ${({ theme }) => theme.colors.primary}10;
    border-color: ${({ theme }) => theme.colors.primary}50;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.75rem;
  }
`;

const MonthDisplay = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-family: ${({ theme }) => theme.fonts.heading};
  min-width: 150px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.25rem;
    min-width: auto;
  }
`;

const DownloadButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  border: none;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: ${({ theme }) => theme.colors.primary}dd;
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.75rem;
  }
`;

const ClosedDayBadge = styled.span`
  display: inline-block;
  padding: 0.15rem 0.4rem;
  border-radius: 999px;
  font-size: 0.6rem;
  font-weight: 600;
  background: ${({ theme }) => theme.colors.textMuted}20;
  color: ${({ theme }) => theme.colors.textMuted};
  border: 1px solid ${({ theme }) => theme.colors.textMuted}40;
  white-space: nowrap;
  text-decoration: line-through;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    font-size: 0.5rem;
    padding: 0.1rem 0.3rem;
  }
`;

const DayCard = styled.div`
  background: ${({ theme, isClosed }) => 
    isClosed ? theme.colors.surfaceMuted : theme.colors.surface};
  border-radius: 1rem;
  padding: 1.5rem;
  border: 2px solid ${({ theme, isClosed }) => 
    isClosed ? theme.colors.textMuted + '30' : theme.colors.primary + '15'};
  transition: ${({ theme }) => theme.transitions.default};
  position: relative;
  overflow: hidden;
  opacity: ${({ isClosed }) => isClosed ? 0.6 : 1};
  text-align: center;
  display: flex;
  flex-direction: column;
  min-height: 200px;
  word-break: keep-all;
  white-space: normal;

  @media (max-width: 768px) {
    min-height: 180px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${({ theme, isClosed }) => 
      isClosed 
        ? theme.colors.textMuted 
        : `linear-gradient(90deg, ${theme.colors.primary}, ${theme.colors.secondary})`};
  }

  &:hover {
    border-color: ${({ theme, isClosed }) => 
      isClosed ? theme.colors.textMuted + '50' : theme.colors.primary + '30'};
    box-shadow: ${({ theme }) => theme.shadows.md};
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
    min-height: 180px;
  }
`;

const DayName = styled.h3`
  margin: 0 0 0.75rem;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: ${({ isCompact }) => isCompact ? '1rem' : '1.25rem'};
  color: ${({ theme, isClosed }) => 
    isClosed ? theme.colors.textMuted : theme.colors.textPrimary};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-weight: 600;
  line-height: 1.4;
  word-break: keep-all;

  @media (max-width: 768px) {
    font-size: 0.875rem;
    gap: 0.4rem;
    line-height: 1.3;
  }
`;

const DayDivider = styled.div`
  width: 100%;
  height: 2px;
  background: ${({ theme, isClosed }) => 
    isClosed 
      ? theme.colors.textMuted + '30'
      : `linear-gradient(90deg, transparent, ${theme.colors.primary}50, transparent)`};
  margin: 0.25rem 0;
  
  @media (max-width: 768px) {
    height: 1.5px;
  }
`;

const ClassTimeGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.5rem;
  flex: 1;
  justify-content: center;
`;

const ClassTimeItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
`;

const ClassTypeBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  background: ${({ theme, type }) => 
    type === 'elementary' 
      ? `${theme.colors.primary}15` 
      : `${theme.colors.secondary}15`};
  color: ${({ theme, type }) => 
    type === 'elementary' ? theme.colors.primary : theme.colors.secondary};
  border: 1px solid ${({ theme, type }) => 
    type === 'elementary' 
      ? `${theme.colors.primary}30` 
      : `${theme.colors.secondary}30`};
  
  @media (max-width: 768px) {
    font-size: 0.625rem;
    padding: 0.2rem 0.6rem;
  }
`;

const ClassTimeValue = styled.span`
  font-size: ${({ isCompact }) => isCompact ? '0.875rem' : '1rem'};
  font-weight: 600;
  color: ${({ theme, type }) => 
    type === 'elementary' ? theme.colors.primary : theme.colors.secondary};
  line-height: 1.5;
  word-break: keep-all;
  white-space: normal;
  display: block;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    line-height: 1.4;
  }
`;


const ClosedLabel = styled.div`
  margin-top: auto;
  padding-top: 0.75rem;
  border-top: 1px solid ${({ theme }) => theme.colors.surface};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  font-weight: 500;
  text-decoration: line-through;
  word-break: keep-all;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding-top: 0.5rem;
  }
`;

const SchedulePage = () => {
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth()); // 0-based (0 = 1월, 11 = 12월)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isDownloading, setIsDownloading] = useState(false);
  const calendarRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const weeklySchedule = [
    { 
      day: '월요일', 
      shortDay: '월', 
      dayIndex: 1,
      icon: '📆', 
      hasRegularClass: true, 
      isClosed: false,
      elementary: '2~7시',
      middleHigh: '4~10시'
    },
    { 
      day: '화요일', 
      shortDay: '화', 
      dayIndex: 2,
      icon: '📆', 
      hasRegularClass: true, 
      isClosed: false,
      elementary: '2~7시',
      middleHigh: '4~10시'
    },
    { 
      day: '수요일', 
      shortDay: '수', 
      dayIndex: 3,
      icon: '⛔', 
      hasRegularClass: false, 
      isClosed: true,
      elementary: '',
      middleHigh: ''
    },
    { 
      day: '목요일', 
      shortDay: '목', 
      dayIndex: 4,
      icon: '📆', 
      hasRegularClass: true, 
      isClosed: false,
      elementary: '2~7시',
      middleHigh: '4~10시'
    },
    { 
      day: '금요일', 
      shortDay: '금', 
      dayIndex: 5,
      icon: '📆', 
      hasRegularClass: true, 
      isClosed: false,
      elementary: '2~7시',
      middleHigh: '4~10시'
    },
    { 
      day: '토요일', 
      shortDay: '토', 
      dayIndex: 6,
      icon: '📆', 
      hasRegularClass: true, 
      isClosed: false,
      elementary: '1~3시',
      middleHigh: '10~3시'
    },
    { 
      day: '일요일', 
      shortDay: '일', 
      dayIndex: 0,
      icon: '⛔', 
      hasRegularClass: false, 
      isClosed: true,
      elementary: '',
      middleHigh: ''
    },
  ];

  // 법정 공휴일 확인 함수
  const getHolidays = (year, month) => {
    const holidays = {};
    
    // 2025년 법정 공휴일
    if (year === 2025) {
      // 12월
      if (month === 11) {
        holidays[25] = '크리스마스';
      }
    }
    
    // 2026년 법정 공휴일
    if (year === 2026) {
      // 1월
      if (month === 0) {
        holidays[1] = '신정';
      }
      // 3월
      if (month === 2) {
        holidays[1] = '삼일절';
        holidays[2] = '대체공휴일';
      }
      // 5월
      if (month === 4) {
        holidays[1] = '노동절';
        holidays[5] = '어린이날';
        holidays[24] = '부처님오신날';
        holidays[25] = '대체공휴일';
      }
      // 6월
      if (month === 5) {
        holidays[3] = '지방선거';
        holidays[6] = '현충일';
      }
      // 7월
      if (month === 6) {
        holidays[17] = '제헌절';
      }
      // 8월
      if (month === 7) {
        holidays[15] = '광복절';
        holidays[17] = '대체공휴일';
      }
      // 10월
      if (month === 9) {
        holidays[3] = '개천절';
        holidays[9] = '한글날';
      }
      // 12월
      if (month === 11) {
        holidays[25] = '크리스마스';
      }
    }
    
    return holidays;
  };

  // 특별 일정 데이터
  const getSpecialEvents = (year, month) => {
    const events = {};
    const holidays = getHolidays(year, month);
    
    // 법정 공휴일 추가
    Object.keys(holidays).forEach(day => {
      events[parseInt(day)] = events[parseInt(day)] || [];
      events[parseInt(day)].push({ type: 'holiday', label: holidays[day] });
    });
    
    // 2025년 12월 일정
    if (year === 2025 && month === 11) {
      // 토요일마다 특강
      for (let day = 1; day <= 31; day++) {
        const date = new Date(year, month, day);
        if (date.getDay() === 6) { // 토요일
          events[day] = events[day] || [];
          events[day].push({ type: 'lecture', label: '특강' });
        }
      }
      
      // 13일 COS 시험
      events[13] = events[13] || [];
      events[13].push({ type: 'exam', label: 'COS 시험' });
      
      // 24일 특별수업
      events[24] = events[24] || [];
      events[24].push({ type: 'special', label: '특별수업' });
      
      // 25, 26, 29, 30일 휴원
      [25, 26, 29, 30].forEach(day => {
        events[day] = events[day] || [];
        events[day].push({ type: 'closed', label: '휴원' });
      });
    }
    
    // 2026년 1월 일정
    if (year === 2026 && month === 0) {
      // 1, 2, 3일 휴원
      [1, 2, 3].forEach(day => {
        events[day] = events[day] || [];
        events[day].push({ type: 'closed', label: '휴원' });
      });
      
      // 3일 COS 시험
      events[3] = events[3] || [];
      events[3].push({ type: 'exam', label: 'COS 시험' });
      
      // 10, 17, 24, 31일 특강
      [10, 17, 24, 31].forEach(day => {
        events[day] = events[day] || [];
        events[day].push({ type: 'lecture', label: '특강' });
      });
      
      // 31일 AICE 시험
      events[31] = events[31] || [];
      events[31].push({ type: 'exam', label: 'AICE 시험' });
    }
    
    // 2026년 3월 31일 휴원
    if (year === 2026 && month === 2) {
      events[31] = events[31] || [];
      events[31].push({ type: 'closed', label: '휴원' });
    }
    
    // 2026년 4월 30일 휴원
    if (year === 2026 && month === 3) {
      events[30] = events[30] || [];
      events[30].push({ type: 'closed', label: '휴원' });
    }

    // 2026년 5월 일정
    if (year === 2026 && month === 4) {
      // 4, 7, 8일 특별수업
      [2, 4, 7, 8].forEach((day) => {
        events[day] = events[day] || [];
        events[day].push({ type: 'special', label: '특별수업' });
      });

      // 30일: 정규수업 없음 + 시험
      events[30] = events[30] || [];
      events[30].push({ type: 'noRegular', label: '' });
      events[30].push({ type: 'exam', label: '시험' });
    }

    // 2026년 7월 5주차(30, 31일) 휴원
    if (year === 2026 && month === 6) {
      [30, 31].forEach(day => {
        events[day] = events[day] || [];
        events[day].push({ type: 'closed', label: '휴원' });
      });
    }
    
    return events;
  };

  // 현재 날짜 기준으로 캘린더 생성
  const getCalendarDays = () => {
    // 이번 달의 첫 번째 날과 마지막 날
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay(); // 0 = 일요일, 1 = 월요일, ...
    
    const days = [];
    const specialEvents = getSpecialEvents(currentYear, currentMonth);
    
    // 첫 주의 빈 칸들
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    
    // 이번 달의 모든 날짜
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const dayOfWeek = date.getDay();
      const daySchedule = weeklySchedule.find(s => s.dayIndex === dayOfWeek);
      const dayEvents = specialEvents[day] || [];
      const isClosed = dayEvents.some(e => e.type === 'closed');
      const hasSpecialClass = dayEvents.some(e => e.type === 'special');
      const isHoliday = dayEvents.some(e => e.type === 'holiday');
      const noRegularClass = dayEvents.some(e => e.type === 'noRegular');
      
      days.push({
        day,
        dayOfWeek,
        hasClass: (daySchedule?.hasRegularClass || false) && !isClosed && !isHoliday && !noRegularClass,
        isToday: day === today.getDate() && currentMonth === today.getMonth() && currentYear === today.getFullYear(),
        isClosed,
        hasSpecialClass,
        isHoliday,
        elementary: daySchedule?.elementary || '',
        middleHigh: daySchedule?.middleHigh || '',
        events: dayEvents
      });
    }
    
    return days;
  };

  const calendarDays = getCalendarDays();
  const weekDays = ['일', '월', '화', '수', '목', '금', '토'];
  const monthNames = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleDownloadCalendar = async () => {
    if (!calendarRef.current || isDownloading) return;
    
    setIsDownloading(true);
    try {
      // 캘린더를 캔버스로 변환
      const calendarCanvas = await html2canvas(calendarRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
      });

      // 로고 이미지 로드
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      
      // 로고 이미지 로드 대기
      await new Promise((resolve) => {
        logoImg.onload = resolve;
        logoImg.onerror = () => {
          console.warn('로고 이미지를 로드할 수 없습니다. 로고 없이 진행합니다.');
          resolve();
        };
        // 에이스 코딩학원 로고 사용
        logoImg.src = '/logo_394F74.png';
      });

      // 새로운 캔버스 생성 (로고 공간 포함)
      const logoSize = 80;
      const padding = 20;
      const logoHeight = logoImg.complete && logoImg.naturalWidth > 0 ? logoSize + padding * 2 : 0;
      
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = calendarCanvas.width;
      finalCanvas.height = calendarCanvas.height + logoHeight;
      const ctx = finalCanvas.getContext('2d');

      // 배경을 흰색으로
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);

      // 로고가 있으면 그리기
      if (logoImg.complete && logoImg.naturalWidth > 0) {
        const logoX = padding;
        const logoY = padding;
        ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
      }

      // 캘린더 이미지를 로고 아래에 그리기
      ctx.drawImage(calendarCanvas, 0, logoHeight);

      // 다운로드
      const link = document.createElement('a');
      const fileName = `에이스코딩학원_일정표_${currentYear}년_${monthNames[currentMonth]}.png`;
      link.download = fileName;
      link.href = finalCanvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('다운로드 중 오류가 발생했습니다:', error);
      alert('다운로드 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Wrapper>
      <ScheduleSection>
        <SectionHeading 
          eyebrow="주간 일정" 
          title="수업 운영 시간" 
          description={
            <>
              에이스 코딩학원의 주간 수업 운영 시간표입니다.
              <br />
              월요일, 화요일, 목요일, 금요일, 토요일에 정규수업이 운영됩니다.
            </>
          }
        />
        <WeeklyScheduleGrid>
          {weeklySchedule.map((schedule) => (
            <DayCard key={schedule.day} isClosed={schedule.isClosed}>
              <DayName isClosed={schedule.isClosed} isCompact>
                {schedule.day}
                <DayDivider isClosed={schedule.isClosed} />
              </DayName>
              {schedule.isClosed ? (
                <ClosedLabel>휴원일</ClosedLabel>
              ) : (
                <ClassTimeGroup>
                  <ClassTimeItem>
                    <ClassTypeBadge type="elementary">초등</ClassTypeBadge>
                    <ClassTimeValue isCompact type="elementary">
                      {schedule.elementary}
                    </ClassTimeValue>
                  </ClassTimeItem>
                  <ClassTimeItem>
                    <ClassTypeBadge type="middleHigh">중고등</ClassTypeBadge>
                    <ClassTimeValue isCompact type="middleHigh">
                      {schedule.middleHigh}
                    </ClassTimeValue>
                  </ClassTimeItem>
                </ClassTimeGroup>
              )}
            </DayCard>
          ))}
        </WeeklyScheduleGrid>
        <NoticeText>
          * 성인 수업 시간표 별도 문의 부탁드립니다
        </NoticeText>
      </ScheduleSection>

      <ScheduleSection>
        <SectionHeading 
          eyebrow="월별 일정" 
          title="정규수업 일정" 
          description="월요일, 화요일, 목요일, 금요일, 토요일에 정규수업이 진행됩니다."
        />
        <CalendarContainer ref={calendarRef}>
          <CalendarHeaderControls>
            <MonthSelector>
              <MonthButton onClick={handlePrevMonth}>이전</MonthButton>
              <MonthDisplay>{currentYear}년 {monthNames[currentMonth]}</MonthDisplay>
              <MonthButton onClick={handleNextMonth}>다음</MonthButton>
            </MonthSelector>
            <DownloadButton onClick={handleDownloadCalendar} disabled={isDownloading}>
              {isDownloading ? '다운로드 중...' : '📥 달력 다운로드'}
            </DownloadButton>
          </CalendarHeaderControls>
          <LegendContainer>
            <LegendItem>
              <LegendBadge type="elementary">초</LegendBadge>
              <LegendLabel>초등</LegendLabel>
            </LegendItem>
            <LegendItem>
              <LegendBadge type="middle">중</LegendBadge>
              <LegendLabel>중등</LegendLabel>
            </LegendItem>
            <LegendItem>
              <LegendBadge type="high">고</LegendBadge>
              <LegendLabel>고등</LegendLabel>
            </LegendItem>
            <LegendItem>
              <LegendBadge eventType="lecture">특</LegendBadge>
              <LegendLabel>특강</LegendLabel>
            </LegendItem>
            <LegendItem>
              <LegendBadge eventType="special">특</LegendBadge>
              <LegendLabel>특별수업</LegendLabel>
            </LegendItem>
            <LegendItem>
              <LegendBadge eventType="exam">시</LegendBadge>
              <LegendLabel>시험</LegendLabel>
            </LegendItem>
            <LegendItem>
              <LegendBadge style={{ textDecoration: 'line-through' }}>휴원</LegendBadge>
              <LegendLabel>휴원</LegendLabel>
            </LegendItem>
          </LegendContainer>
          <CalendarHeader>
            {weekDays.map((day) => (
              <CalendarHeaderCell key={day}>{day}</CalendarHeaderCell>
            ))}
          </CalendarHeader>
          <CalendarGrid>
            {calendarDays.map((dayData, index) => {
              if (dayData === null) {
                return <EmptyDay key={`empty-${index}`} />;
              }
              
              // 배지 순서: 시험 → 특강 → 정규수업
              const examEvents = dayData.events?.filter(e => e.type === 'exam') || [];
              const lectureEvents = dayData.events?.filter(e => e.type === 'lecture') || [];
              const specialEvents = dayData.events?.filter(e => e.type === 'special') || [];
              
              // 배지 순서: 시험 → 특강 → 특별수업 → 정규수업
              const holidayEvents = dayData.events?.filter(e => e.type === 'holiday') || [];
              
              return (
                <CalendarDayCell
                  key={dayData.day}
                  hasClass={dayData.hasClass}
                  isToday={dayData.isToday}
                  isClosed={dayData.isClosed}
                  hasSpecialClass={dayData.hasSpecialClass}
                  isHoliday={dayData.isHoliday}
                >
                  <DayNumber 
                    hasClass={dayData.hasClass} 
                    isToday={dayData.isToday} 
                    isClosed={dayData.isClosed}
                    isHoliday={dayData.isHoliday}
                    dayOfWeek={dayData.dayOfWeek}
                    hasSpecialClass={dayData.hasSpecialClass}
                  >
                    {dayData.day}
                    {dayData.isHoliday && holidayEvents.length > 0 && (
                      <HolidayName>{holidayEvents[0].label}</HolidayName>
                    )}
                  </DayNumber>
                  {dayData.isHoliday && (
                    <ClosedDayBadge>휴원</ClosedDayBadge>
                  )}
                  {!dayData.isHoliday && dayData.isClosed && (
                    <ClosedDayBadge>휴원</ClosedDayBadge>
                  )}
                  <ClassBadges useCompact={(() => {
                    const totalBadges = examEvents.length + lectureEvents.length + specialEvents.length + 
                      (!dayData.isHoliday && !dayData.isClosed && dayData.hasClass ? (isMobile ? 3 : 2) : 0);
                    // 배지가 4개 이상이거나 모바일일 때만 축약 표시
                    return totalBadges >= 4 || isMobile;
                  })()}>
                    {(() => {
                      // 배지 총 개수 계산
                      const totalBadges = examEvents.length + lectureEvents.length + specialEvents.length + 
                        (!dayData.isHoliday && !dayData.isClosed && dayData.hasClass ? (isMobile ? 3 : 2) : 0);
                      // 배지가 4개 이상이거나 모바일일 때만 축약 표시
                      const useCompact = totalBadges >= 4 || isMobile;
                      
                      // 시험 라벨 축약 함수
                      const getExamLabel = (label) => {
                        if (!useCompact) return label;
                        if (label.includes('COS')) return 'C';
                        if (label.includes('AICE')) return 'A';
                        return '시';
                      };
                      
                      return (
                        <>
                          {/* 시험 배지 */}
                          {examEvents.map((event, idx) => (
                            <EventBadge key={`exam-${idx}`} eventType={event.type}>
                              {getExamLabel(event.label)}
                            </EventBadge>
                          ))}
                          {/* 특강 배지 */}
                          {lectureEvents.map((event, idx) => (
                            <EventBadge key={`lecture-${idx}`} eventType={event.type}>
                              {useCompact ? '특' : event.label}
                            </EventBadge>
                          ))}
                          {/* 특별수업 배지 */}
                          {specialEvents.map((event, idx) => (
                            <EventBadge key={`special-${idx}`} eventType={event.type}>
                              {useCompact ? '특' : event.label}
                            </EventBadge>
                          ))}
                          {/* 정규수업 배지 - 휴원일이 아닐 때만 표시 */}
                          {!dayData.isHoliday && !dayData.isClosed && dayData.hasClass && (
                            <>
                              <CalendarBadge type="elementary">
                                {useCompact ? '초' : '초등'}
                              </CalendarBadge>
                              {useCompact ? (
                                <>
                                  <CalendarBadge type="middleHigh">중</CalendarBadge>
                                  <CalendarBadge type="middleHigh">고</CalendarBadge>
                                </>
                              ) : (
                                <CalendarBadge type="middleHigh">중고등</CalendarBadge>
                              )}
                            </>
                          )}
                        </>
                      );
                    })()}
                  </ClassBadges>
                </CalendarDayCell>
              );
            })}
          </CalendarGrid>
        </CalendarContainer>
      </ScheduleSection>
    </Wrapper>
  );
};

export default SchedulePage;

